import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

/**
 * Reusable prompt/confirm dialog for the admin console.
 *
 * Usage (component pattern):
 *
 *   const [prompt, setPrompt] = useState(null);
 *   ...
 *   setPrompt({
 *     title: "Reason for revoking?",
 *     placeholder: "e.g. Requested by partner",
 *     mode: "text",              // "text" | "textarea" | "confirm"
 *     confirmLabel: "Revoke",
 *     tone: "danger",            // "primary" | "danger"
 *     onConfirm: (value) => act(...),
 *   });
 *   ...
 *   <PromptDialog prompt={prompt} onClose={() => setPrompt(null)} />
 */
export default function PromptDialog({ prompt, onClose }) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const active = !!prompt;

  const submit = async () => {
    if (busy) return;
    if (prompt.required && !value.trim()) return;
    setBusy(true);
    try {
      await prompt.onConfirm(value.trim());
      setValue("");
      onClose();
    } finally {
      setBusy(false);
    }
  };

  const btnClass = prompt?.tone === "danger"
    ? "bg-[#991B1B] hover:bg-[#7F1D1D] text-white"
    : "bg-[#1E3A8A] hover:bg-[#172554] text-white";

  return (
    <Dialog open={active} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent data-testid="prompt-dialog"
                       className="sm:max-w-[440px] bg-white">
        <DialogHeader>
          <DialogTitle className="text-[#0F172A]">{prompt?.title}</DialogTitle>
          {prompt?.description && (
            <DialogDescription className="text-sm text-slate-500 pt-1">
              {prompt.description}
            </DialogDescription>
          )}
        </DialogHeader>

        {prompt?.mode === "text" && (
          <input type="text" autoFocus value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={prompt.placeholder || ""}
                    data-testid="prompt-dialog-input"
                    className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30" />
        )}
        {prompt?.mode === "textarea" && (
          <textarea rows={3} autoFocus value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder={prompt.placeholder || ""}
                    data-testid="prompt-dialog-textarea"
                    className="w-full px-3 py-2 rounded-lg border border-[#E5E5E0] text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30" />
        )}
        {prompt?.mode === "confirm" && prompt.body && (
          <div className="text-sm text-slate-700" data-testid="prompt-dialog-body">
            {prompt.body}
          </div>
        )}

        <DialogFooter className="flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy}
            data-testid="prompt-dialog-cancel"
            className="px-4 py-2 rounded-lg text-sm border border-[#E5E5E0] hover:bg-slate-50">
            {prompt?.cancelLabel || "Cancel"}
          </button>
          <button type="button" onClick={submit}
            disabled={busy || (prompt?.required && !value.trim())}
            data-testid="prompt-dialog-confirm"
            className={`px-4 py-2 rounded-lg text-sm font-medium ${btnClass} disabled:opacity-50`}>
            {busy ? "…" : (prompt?.confirmLabel || "OK")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
